// Where the picture actually is inside a <video> element.
//
// A shared screen is rendered with object-fit: contain (see
// .MeetTileVideoContain), so the element is almost never the same shape as the
// stream inside it - there are black bars on two sides, and their size depends
// on both the sharer's screen ratio and the viewer's window. Annotating in
// element coordinates would therefore put everyone's marks in a different
// place, and anything drawn in a bar would point at nothing at all.
//
// Everything in the annotation protocol is normalized against the rect this
// returns, which is the one coordinate space every participant agrees on no
// matter what their window is doing.
export function videoContentRect(video) {
    if (!video) return null;
    const { videoWidth, videoHeight, clientWidth, clientHeight } = video;
    // Zero until the first frame has decoded - annotating before then would be
    // dividing by an unknown aspect ratio.
    if (!videoWidth || !videoHeight || !clientWidth || !clientHeight) return null;

    const scale = Math.min(clientWidth / videoWidth, clientHeight / videoHeight);
    const width = videoWidth * scale;
    const height = videoHeight * scale;
    return {
        x: (clientWidth - width) / 2,
        y: (clientHeight - height) / 2,
        width,
        height
    };
}

// Element-relative pixels -> 0..1 within the picture. Returns null for a point
// in the letterbox bars, which is a deliberate refusal rather than a clamp: a
// mark there has no meaning on the sharer's screen, so it should not be sent
// at all.
export function toNormalized(video, offsetX, offsetY) {
    const rect = videoContentRect(video);
    if (!rect) return null;
    const x = (offsetX - rect.x) / rect.width;
    const y = (offsetY - rect.y) / rect.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x, y };
}

// The inverse, for painting a received mark onto this viewer's canvas.
export function fromNormalized(rect, x, y) {
    return { x: rect.x + x * rect.width, y: rect.y + y * rect.height };
}
